import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalCloseButton, Box, Flex, Text, Spinner, Center,
  SimpleGrid, Collapse, Button, Image, Icon, HStack, VStack, Menu, MenuButton, MenuList, MenuItem,
  useBreakpointValue,
} from '@chakra-ui/react';
import { FiPhone, FiMapPin, FiMoreVertical, FiTrash2, FiX, FiDownload, FiExternalLink, FiAlertCircle } from 'react-icons/fi';
import { FaFilePdf } from 'react-icons/fa';
import { API_URL_BASE, API_URL, deleteSurvey } from 'services/surveyService';
import { getColorForCategory } from 'services/surveyCategoriesService';
import { getCategoryDisplayName } from 'utils/site';
import FichaFieldValue, { getFileIcon, buildImagePreviewUrl } from 'components/Fichas/fichaFieldRenderer';

const CHAKRA_TO_PASTEL = {
  blue: 'sky', cyan: 'sky', teal: 'teal', green: 'mint', red: 'rose',
  pink: 'rose', orange: 'butter', yellow: 'butter', purple: 'lav', gray: 'cream',
};
const EMBEDDABLE_FORMATS = ['pdf', 'txt', 'csv', 'json', 'xml', 'html', 'htm'];
const pastelForCategory = (category) => CHAKRA_TO_PASTEL[getColorForCategory(category)] || 'sky';

const ModalSurveyResponse = ({ isOpen, onClose, customer, data, onSurveyDeleted }) => {
  const [showMeta, setShowMeta] = useState(false);

  // Preview de archivos adjuntos / PDF generado (modal apilado sobre la ficha)
  const [previewData, setPreviewData] = useState(null);
  const [previewType, setPreviewType] = useState(null); // 'file' o 'pdf'
  const [previewStatus, setPreviewStatus] = useState('idle'); // idle | loading | ready | error
  const [blobUrl, setBlobUrl] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  const isPreviewOpen = !!previewData;
  const isMobileOrTablet = useBreakpointValue({ base: true, lg: false });

  const handleOpenFilePreview = (filename) => {
    if (!filename) return;
    setPreviewData({
      fileUrl: buildImagePreviewUrl(filename, API_URL_BASE),
      fileName: filename.split('/').pop(),
      filename,
      created_at: data?.created_at || new Date().toISOString(),
    });
    setPreviewType('file');
  };

  const handleOpenPdfPreview = () => {
    if (!data?.surveyResponseId) return;
    const pdfUrl = `${API_URL}/generate-pdf/${data.surveyResponseId}/survey-default`;
    const fileName = `Survey_${data.surveyResponseId}_${customer?.first_name}_${customer?.last_name}.pdf`;
    setPreviewData({ fileUrl: pdfUrl, fileName, filename: fileName, created_at: data?.created_at || new Date().toISOString() });
    setPreviewType('pdf');
  };

  const handleClosePreview = useCallback(() => {
    setPreviewData(null);
    setPreviewType(null);
    setPreviewStatus('idle');
    setBlobUrl(null);
  }, []);

  // Al cerrar el modal principal o cambiar de ficha no debe quedar un preview anterior
  useEffect(() => {
    if (!isOpen) setShowMeta(false);
    handleClosePreview();
  }, [isOpen, data?.surveyResponseId, handleClosePreview]);

  // Los formatos embebibles se descargan como blob: permite mostrar carga y error reales
  const previewUrl = previewData?.fileUrl;
  const previewFormat = previewData?.fileName?.split('.').pop().toLowerCase();
  const isEmbeddable = EMBEDDABLE_FORMATS.includes(previewFormat);
  useEffect(() => {
    if (!previewUrl || !isEmbeddable) {
      setPreviewStatus(previewUrl ? 'ready' : 'idle');
      return undefined;
    }
    const controller = new AbortController();
    let objectUrl = null;
    setPreviewStatus('loading');
    setBlobUrl(null);
    fetch(previewUrl, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
        setPreviewStatus('ready');
      })
      .catch((err) => {
        if (err.name !== 'AbortError') setPreviewStatus('error');
      });
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [previewUrl, isEmbeddable, retryKey]);

  const openUrl = isEmbeddable && blobUrl ? blobUrl : previewData?.fileUrl;

  const handleDownload = () => {
    if (!previewData) return;
    const link = document.createElement('a');
    link.href = openUrl;
    link.download = previewData.fileName;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteSurvey = async () => {
    const wasDeleted = await deleteSurvey(data?.surveyResponseId);
    if (wasDeleted) {
      onClose();
      onSurveyDeleted?.();
    }
  };

  const renderPreviewContent = () => {
    if (!previewData) return null;
    const fileFormat = previewFormat;

    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'ico', 'tiff', 'heic', 'heif'].includes(fileFormat)) {
      return (
        <Center h="full" bg="white" p={4} overflow="auto">
          <Image src={previewData.fileUrl} alt={previewData.fileName} maxW="100%" maxH="100%" objectFit="contain"
            fallback={<Spinner size="xl" color="coral.500" />} />
        </Center>
      );
    }
    if (isEmbeddable) {
      if (previewStatus === 'loading' || previewStatus === 'idle') {
        return (
          <Center h="full" flexDirection="column" gap="12px">
            <Spinner size="xl" color="coral.500" thickness="3px" />
            <Text fontSize="13px" fontWeight="600" color="ink.400">
              {previewType === 'pdf' ? 'Generando PDF…' : 'Cargando archivo…'}
            </Text>
          </Center>
        );
      }
      if (previewStatus === 'error') {
        return (
          <Center h="full" flexDirection="column" textAlign="center" p={8}>
            <Icon as={FiAlertCircle} boxSize={12} color="danger.500" mb={4} />
            <Text fontSize="14px" fontWeight="700" color="ink.900" mb={1}>No se pudo cargar el archivo</Text>
            <Text fontSize="12.5px" color="ink.400" mb={5}>Revisa tu conexión e inténtalo nuevamente.</Text>
            <HStack spacing="10px">
              <Button bg="coral.500" color="white" borderRadius="10px" fontWeight="700"
                _hover={{ bg: 'coral.600' }} onClick={() => setRetryKey(k => k + 1)}>
                Reintentar
              </Button>
              <Button as="a" href={previewData.fileUrl} target="_blank" rel="noopener noreferrer"
                variant="outline" borderRadius="10px" fontWeight="700">
                Abrir en pestaña nueva
              </Button>
            </HStack>
          </Center>
        );
      }
      // Los visores de PDF embebidos fallan en móvil/tablet: se ofrece abrir o descargar
      if (isMobileOrTablet && fileFormat === 'pdf') {
        return (
          <Center h="full" flexDirection="column" textAlign="center" p={8}>
            <Icon as={getFileIcon(previewData.fileName)} boxSize={14} color="coral.500" mb={4} />
            <Text fontSize="14px" fontWeight="700" color="ink.900" mb={1}>PDF listo</Text>
            <Text fontSize="12.5px" color="ink.400" mb={5}>
              Tu dispositivo no muestra PDF dentro de la página. Ábrelo en una pestaña nueva.
            </Text>
            <Button as="a" href={blobUrl} target="_blank" rel="noopener noreferrer" bg="coral.500" color="white"
              borderRadius="10px" fontWeight="700" _hover={{ bg: 'coral.600' }} leftIcon={<Icon as={FiExternalLink} />}>
              Abrir PDF
            </Button>
          </Center>
        );
      }
      return (
        <iframe src={blobUrl} title={previewData.fileName}
          style={{ border: 'none', width: '100%', height: '100%', display: 'block', background: 'white' }} />
      );
    }
    if (['mp3', 'wav', 'ogg', 'aac', 'm4a', 'flac'].includes(fileFormat)) {
      return (
        <Center h="full" flexDirection="column" bg="white" p={8} textAlign="center">
          <Icon as={getFileIcon(previewData.fileName)} boxSize={14} color="coral.500" mb={5} />
          <Text fontSize="14px" fontWeight="700" color="ink.900" mb={4}>{previewData.fileName}</Text>
          <audio controls style={{ width: '100%', maxWidth: '400px' }} preload="metadata">
            <source src={previewData.fileUrl} type={`audio/${fileFormat}`} />
            Tu navegador no soporta la reproducción de audio.
          </audio>
        </Center>
      );
    }
    return (
      <Center h="full" flexDirection="column" bg="white" p={8} textAlign="center">
        <Icon as={getFileIcon(previewData.fileName)} boxSize={14} color="ink.400" mb={4} />
        <Text fontSize="14px" fontWeight="700" color="ink.900" mb={1}>Vista previa no disponible</Text>
        <Text fontSize="12.5px" color="ink.400" mb={5}>Este tipo de archivo no se puede visualizar en el navegador.</Text>
        <Button onClick={handleDownload} bg="coral.500" color="white" borderRadius="10px" fontWeight="700"
          _hover={{ bg: 'coral.600' }}>
          Descargar para ver
        </Button>
      </Center>
    );
  };

  if (!data) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent maxW="360px" borderRadius="20px" bg="white" p={10}>
          <Center><Spinner size="xl" color="coral.500" /></Center>
        </ModalContent>
      </Modal>
    );
  }

  if (data.error) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent maxW="420px" borderRadius="20px" bg="white" p={8}>
          <ModalCloseButton borderRadius="10px" />
          <Text fontSize="14px" fontWeight="700" color="danger.500">{data.error}</Text>
        </ModalContent>
      </Modal>
    );
  }

  const sectionsMap = (data?.questions || []).reduce((acc, question) => {
    const sectionId = question.section.id;
    if (!acc[sectionId]) acc[sectionId] = { name: question.section.name, questions: [] };
    acc[sectionId].questions.push(question);
    return acc;
  }, {});

  const metaQuestions = (data?.questions || []).filter(q => q.text.includes('meta_data'));
  const mainSections = Object.values(sectionsMap)
    .map(section => ({ ...section, questions: section.questions.filter(q => !q.text.includes('meta_data')) }))
    .filter(section => section.questions.length > 0);

  const pastel = pastelForCategory(data?.survey?.category);
  const rawCategoryLabel = getCategoryDisplayName(data?.survey?.category);
  const categoryLabel = rawCategoryLabel?.includes(' ') ? rawCategoryLabel.split(' ').slice(1).join(' ') : rawCategoryLabel;

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside" isCentered>
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent maxW="90vw" maxH="90vh" bg="white" borderRadius="20px"
        overflow="hidden" margin="auto" fontFamily="jakarta">

        <Flex h="62px" align="center" gap="12px" px="24px" bg="white" borderBottom="1px solid"
          borderColor="cream.line" flexShrink={0}>
          <Box minW={0}>
            <Text fontWeight="800" fontSize="14.5px" color="ink.900" noOfLines={1}>
              {data?.survey?.name || 'Ficha'}
            </Text>
            <HStack spacing="7px" mt="1px">
              <Box as="span" px="7px" py="1px" borderRadius="999px" bg={`${pastel}.bg`} color={`${pastel}.ink`}
                fontSize="10.5px" fontWeight="700">
                {categoryLabel}
              </Box>
              <Text fontSize="11.5px" color="ink.400" fontWeight="600">#{data?.surveyResponseId}</Text>
            </HStack>
          </Box>
          <Box flex={1} />
          <Button size="sm" bg="coral.500" color="white" borderRadius="10px" fontWeight="700" fontSize="13px"
            _hover={{ bg: 'coral.600' }} leftIcon={<Icon as={FaFilePdf} boxSize="13px" />}
            onClick={handleOpenPdfPreview} aria-label="Ver PDF">
            {isMobileOrTablet ? '' : 'Ver PDF'}
          </Button>
          <Menu>
            <MenuButton as={Button} size="sm" variant="ghost" borderRadius="9px" minW="auto" p="6px" h="32px">
              <Icon as={FiMoreVertical} boxSize="16px" color="ink.400" />
            </MenuButton>
            <MenuList borderRadius="12px" fontSize="13.5px" minW="200px">
              <MenuItem onClick={handleOpenPdfPreview} icon={<Icon as={FaFilePdf} boxSize="14px" />}>
                Ver PDF
              </MenuItem>
              <MenuItem onClick={handleDeleteSurvey} color="danger.500" icon={<Icon as={FiTrash2} boxSize="14px" />}>
                Eliminar formulario
              </MenuItem>
            </MenuList>
          </Menu>
          <ModalCloseButton position="static" borderRadius="10px" size="sm" />
        </Flex>

        <Flex flex={1} minH={0} overflow="hidden">

          <Box flex={1} overflow="auto" bg="cream.bg" p={{ base: '20px', md: '28px' }}>

            <Flex align="center" gap="14px" bg="white" border="1px solid" borderColor="cream.line"
              borderRadius="16px" p="14px 18px" mb="16px">
              <Image src="https://blaspascal.cl/wp-content/uploads/2022/10/logo_relieve-150x150-1-150x150.png"
                boxSize="48px" borderRadius="12px" flexShrink={0} />
              <Box flex={1} minW={0}>
                <Text fontWeight="800" fontSize="14.5px" color="ink.900">Blas Pascal School</Text>
                <Text fontSize="12.5px" color="ink.400" fontWeight="500">Colegio Osorno</Text>
              </Box>
              <VStack spacing="3px" align="flex-end" fontSize="12px" color="ink.400" fontWeight="600" flexShrink={0}>
                <HStack spacing="5px"><Icon as={FiPhone} boxSize="11px" /><Text>+56 9 6762 2809</Text></HStack>
                <HStack spacing="5px"><Icon as={FiMapPin} boxSize="11px" /><Text>Av. Manuel Rodríguez 895, Osorno</Text></HStack>
              </VStack>
            </Flex>

            <Flex align="center" justify="space-between" bg="white" border="1px solid" borderColor="cream.line"
              borderRadius="16px" p="14px 18px" mb="20px" flexWrap="wrap" gap="10px">
              <Box>
                <Text fontWeight="800" fontSize="14.5px" color="ink.900">
                  {customer?.first_name} {customer?.last_name}
                </Text>
                <Text fontSize="12.5px" color="ink.400" fontWeight="500">{customer?.username} · {customer?.email}</Text>
              </Box>
              <HStack spacing="18px">
                <Box textAlign="right">
                  <Text fontSize="10.5px" color="ink.400" fontWeight="700" textTransform="uppercase" letterSpacing="0.05em">Registro</Text>
                  <Text fontSize="13px" fontWeight="800" color="coral.700">N° {data?.surveyResponseId}</Text>
                </Box>
                <Box textAlign="right">
                  <Text fontSize="10.5px" color="ink.400" fontWeight="700" textTransform="uppercase" letterSpacing="0.05em">Fecha de ingreso</Text>
                  <Text fontSize="13px" fontWeight="700" color="ink.900">
                    {data?.created_at ? new Date(data.created_at).toLocaleDateString('es-CL') : '—'}
                  </Text>
                </Box>
              </HStack>
            </Flex>

            <Text fontSize="17px" fontWeight="800" color="ink.900" textAlign="center" mb="20px">
              {data?.survey?.name}
            </Text>

            {mainSections.map((section, index) => (
              <Box key={index} bg="white" border="1px solid" borderColor="cream.line" borderRadius="16px"
                p="16px 18px" mb="14px">
                <Text fontSize="12px" fontWeight="800" letterSpacing="0.05em" textTransform="uppercase"
                  color="ink.400" mb="14px">
                  {section.name}
                </Text>
                <SimpleGrid columns={12} gap="16px">
                  {section.questions.map((question, qIndex) => (
                    <Box key={qIndex} gridColumn={{ base: 'span 12', md: `span ${question.column_width ?? 12}` }}>
                      <Text fontSize="12.5px" fontWeight="700" color="ink.700" mb="6px">{question.text}</Text>
                      <FichaFieldValue question={question} apiUrlBase={API_URL_BASE} onPreviewFile={handleOpenFilePreview} />
                    </Box>
                  ))}
                </SimpleGrid>
              </Box>
            ))}

            {metaQuestions.length > 0 && (
              <>
                <Button size="sm" variant="ghost" color="ink.700" fontWeight="700" borderRadius="9px"
                  onClick={() => setShowMeta(s => !s)} mb="10px">
                  Mostrar {showMeta ? 'menos' : 'más'}
                </Button>
                <Collapse in={showMeta}>
                  <Box bg="cream.bg2" borderRadius="14px" p="14px 18px" mb="14px">
                    {metaQuestions.map((question, index) => (
                      <Box key={index} mb={index < metaQuestions.length - 1 ? '12px' : 0}>
                        <Text fontSize="12px" fontWeight="700" color="ink.400">{question.text}</Text>
                        <Text fontSize="13px" color="ink.700">{question?.response?.answer}</Text>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </>
            )}
          </Box>

        </Flex>
      </ModalContent>
    </Modal>

    <Modal isOpen={isPreviewOpen} onClose={handleClosePreview} size="full" isCentered
      motionPreset="slideInBottom" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent maxW={{ base: '100vw', lg: '92vw' }} h={{ base: '100vh', lg: '92vh' }} my={{ base: 0, lg: 'auto' }}
        bg="cream.bg2" borderRadius={{ base: 0, lg: '20px' }} overflow="hidden" fontFamily="jakarta">
        <Flex h="62px" align="center" gap="12px" px={{ base: '14px', md: '20px' }} bg="white" borderBottom="1px solid"
          borderColor="cream.line" flexShrink={0}>
          <Flex w="36px" h="36px" borderRadius="10px" bg="coral.soft" color="coral.700"
            align="center" justify="center" flexShrink={0}>
            <Icon as={getFileIcon(previewData?.fileName)} boxSize="16px" />
          </Flex>
          <Box minW={0} flex={1}>
            <Text fontSize="13.5px" fontWeight="800" color="ink.900" noOfLines={1}>{previewData?.fileName}</Text>
            <Text fontSize="11.5px" color="ink.400" fontWeight="600">
              {previewData?.fileName?.split('.').pop().toUpperCase()}
            </Text>
          </Box>
          <Button size="sm" variant="ghost" borderRadius="9px" onClick={handleDownload} leftIcon={<Icon as={FiDownload} />}
            isDisabled={isEmbeddable && previewStatus !== 'ready'}>
            {isMobileOrTablet ? '' : 'Descargar'}
          </Button>
          <Button as="a" size="sm" variant="ghost" borderRadius="9px" href={openUrl}
            target="_blank" rel="noopener noreferrer" leftIcon={<Icon as={FiExternalLink} />}
            isDisabled={isEmbeddable && previewStatus !== 'ready'}>
            {isMobileOrTablet ? '' : 'Abrir en pestaña'}
          </Button>
          <Button size="sm" variant="ghost" borderRadius="9px" onClick={handleClosePreview} flexShrink={0} aria-label="Cerrar vista previa">
            <Icon as={FiX} boxSize="16px" />
          </Button>
        </Flex>
        <Box flex={1} minH={0} overflow="hidden">{renderPreviewContent()}</Box>
      </ModalContent>
    </Modal>
    </>
  );
};

export default ModalSurveyResponse;
